import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import * as api from 'api/api'
import { NotificationItem } from 'api/types'
import { Icon } from 'components/Icon'
import { useToast } from 'components/Toast'
import { formatDate } from 'lib/format'
import { tx } from 'lib/i18n'
import { PushStatus, enablePush, onPushMessage, pushStatus } from 'lib/push'

import { useRegion } from '../context/RegionContext'

export const NotificationBell = () => {
  const navigate = useNavigate()
  const notify = useToast()
  const { locale } = useRegion()
  const [open, setOpen] = useState(false)
  const [items, setItems] = useState<NotificationItem[]>([])
  const [push, setPush] = useState<PushStatus>('unsupported')
  const panel = useRef<HTMLDivElement>(null)

  const load = () => api.fetchNotifications().then(setItems).catch(() => undefined)

  useEffect(() => {
    load()
    const timer = setInterval(load, 5 * 60 * 1000)
    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    let active = true
    pushStatus().then((status) => {
      if (!active) return
      setPush(status)
      if (status === 'granted') enablePush().catch(() => undefined)
    })
    const stop = onPushMessage((payload) => {
      load()
      if (payload.data?.title) notify(payload.data.title, 'info')
    })
    return () => {
      active = false
      stop()
    }
  }, [notify])

  const turnOnPush = async () => {
    const enabled = await enablePush().catch(() => false)
    setPush(await pushStatus())
    notify(
      enabled ? tx(locale, 'تم تفعيل إشعارات المتصفح', 'Browser notifications are on') : tx(locale, 'تعذر تفعيل إشعارات المتصفح', 'Could not turn on browser notifications'),
      enabled ? 'success' : 'error'
    )
  }

  useEffect(() => {
    if (!open) return undefined
    const onClick = (event: MouseEvent) => {
      if (!panel.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && setOpen(false)
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const unread = items.filter((item) => !item.readAt).length

  const openItem = async (item: NotificationItem) => {
    setOpen(false)
    if (!item.readAt) await api.markNotificationRead(item.id).catch(() => undefined)
    load()
    navigate(`/projects/${item.projectId}/reports/${item.reportId}`)
  }

  const markAll = async () => {
    await api.markAllNotificationsRead().catch(() => undefined)
    load()
  }

  return (
    <div className="bell" ref={panel}>
      <button
        type="button"
        className="icon-button"
        aria-label={unread ? tx(locale, `التنبيهات (${unread} غير مقروءة)`, `Notifications (${unread} unread)`) : tx(locale, 'التنبيهات', 'Notifications')}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <Icon name="bell" />
        {unread ? <span className="bell__count">{unread > 99 ? '99+' : unread}</span> : null}
      </button>
      {open ? (
        <div className="bell__panel" role="dialog" aria-label={tx(locale, 'التنبيهات', 'Notifications')}>
          <header>
            <strong>{tx(locale, 'التنبيهات', 'Notifications')}</strong>
            {unread ? (
              <button type="button" className="link-button" onClick={markAll}>
                {tx(locale, 'تحديد الكل كمقروء', 'Mark all as read')}
              </button>
            ) : null}
          </header>
          {items.length === 0 ? (
            <p className="bell__empty">{tx(locale, 'لا توجد تنبيهات', 'No notifications')}</p>
          ) : (
            <ul>
              {items.slice(0, 30).map((item) => (
                <li key={item.id}>
                  <button type="button" className={item.readAt ? '' : 'is-unread'} onClick={() => openItem(item)}>
                    <Icon name="alert" />
                    <span>
                      <strong>{tx(locale, `تقرير مفقود: ${item.reportCode}`, `Missing report: ${item.reportCode}`)}</strong>
                      <small>
                        {formatDate(item.reportDate)} · {item.projectName}
                      </small>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {push === 'default' ? (
            <footer className="bell__footer">
              <button type="button" className="link-button" onClick={turnOnPush}>
                {tx(locale, 'تفعيل إشعارات المتصفح', 'Turn on browser notifications')}
              </button>
            </footer>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
